type StorageKind = 'local' | 'session';

function getStorage(kind: StorageKind): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storage = kind === 'local' ? window.localStorage : window.sessionStorage;
    if (!storage) {
      return null;
    }
    // Probe access — Safari private mode / embedded WebViews can throw or block writes.
    const probeKey = '__quali_storage_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return storage;
  } catch {
    return null;
  }
}

export function safeLocalStorageGetItem(key: string): string | null {
  const storage = getStorage('local');
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function safeLocalStorageSetItem(key: string, value: string): boolean {
  const storage = getStorage('local');
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeLocalStorageRemoveItem(key: string): void {
  const storage = getStorage('local');
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

export function safeSessionStorageGetItem(key: string): string | null {
  const storage = getStorage('session');
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function safeSessionStorageSetItem(key: string, value: string): boolean {
  const storage = getStorage('session');
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeSessionStorageRemoveItem(key: string): void {
  const storage = getStorage('session');
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

export function safeClearWebStorage(
  options: { preserveLocalKeys?: string[] } = {},
): void {
  const local = getStorage('local');
  const session = getStorage('session');
  try {
    const preserved = (options.preserveLocalKeys ?? []).flatMap((key) => {
      const value = local?.getItem(key);
      return value == null ? [] : [[key, value] as const];
    });
    local?.clear();
    for (const [key, value] of preserved) {
      local?.setItem(key, value);
    }
  } catch {
    /* ignore */
  }
  try {
    session?.clear();
  } catch {
    /* ignore */
  }
}
